'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { useGSAPMedia } from '@/hooks'
import { useStorePreload } from '@/store'
import { gsap } from '@gsap'
import { MEDIA } from '@spot'
import { memo, useRef } from 'react'

const Preload = () => {
  const ref = useRef<any>(null)

  const preload = useStorePreload.use.preload()
  const setPreload = useStorePreload.use.setPreload()
  const setPreloaded = useStorePreload.use.setIsPreloaded()

  useGSAPMedia({
    type: 'layout',
    media: MEDIA,
    scope: ref,
    callback(_c) {
      const units = '[data-units]'
      const tens = '[data-tens]'
      const hundreds = '[data-hundreds]'
      const text = '[data-text]'
      const bg = '[data-bg]'
      const logoPane = '[data-logo-pane]'

      gsap.set(text, { opacity: 0, y: '2rem' })
      gsap.set(bg, { yPercent: 100, display: 'block' })
      gsap.set(logoPane, { yPercent: 100, opacity: 1 })

      const tl = gsap.timeline({
        defaults: {
          ease: EASE.out,
          duration: DUR.goldenRatio * 2
        }
      })

      tl.to(text, { opacity: 1, y: 0, duration: DUR.default })
        .to(units, { yPercent: -1000 }, DUR.default / 2)
        .to(tens, { yPercent: -500 }, '<')
        .to(
          hundreds,
          { yPercent: -100, ease: EASE.in, duration: DUR.goldenRatio * 1.65 },
          '<'
        )
        .to(bg, { yPercent: 0 }, '<')
        .to([units, tens, hundreds], {
          y: '-4rem',
          duration: DUR.default,
          stagger: DUR.stagger
        })
        .to(logoPane, { yPercent: 0, duration: DUR.goldenRatio / 2 }, '<+=0.33')
        .to(ref.current, {
          autoAlpha: 0,
          duration: DUR.default,
          onComplete: () => {
            setPreloaded(true)
            setPreload(false)
          }
        })
        .to(ref.current, { display: 'none', duration: 0 })
    }
  })

  if (preload === false) {
    return null
  } else {
    return (
      <aside
        className="fixed top-0 left-0 z-[9999] flex h-screen w-screen items-center justify-center bg-black"
        ref={ref}
      >
        <div
          className="fixed bottom-0 left-0 z-0 hidden h-full w-full bg-white"
          data-bg
        />

        <div
          className="relative z-[2] flex h-16 overflow-hidden text-white opacity-0 mix-blend-exclusion"
          data-text
        >
          <div
            className="relative z-[2] flex h-auto flex-col items-end [&_p]:text-[4rem] [&_p]:leading-none [&_p]:font-normal"
            data-hundreds
          >
            <p>0</p>
            <p>1</p>
          </div>
          <div
            className="relative z-[2] flex h-auto flex-col items-end [&_p]:text-[4rem] [&_p]:leading-none [&_p]:font-normal"
            data-tens
          >
            <p>0</p>
            <p>2</p>
            <p>4</p>
            <p>6</p>
            <p>8</p>
            <p>0</p>
          </div>
          <div
            className="relative z-[2] flex h-auto flex-col items-end [&_p]:text-[4rem] [&_p]:leading-none [&_p]:font-normal"
            data-units
          >
            <p>0</p>
            <p>1</p>
            <p>2</p>
            <p>3</p>
            <p>4</p>
            <p>5</p>
            <p>6</p>
            <p>7</p>
            <p>8</p>
            <p>9</p>
            <p>0</p>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 z-[5] flex h-full w-full items-center justify-center bg-neutral-100 opacity-0"
          data-logo-pane
        />
      </aside>
    )
  }
}

export default memo(Preload)
